import React, { useMemo } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { BASE_OPTIONS, EJE_X_BASE, EJE_Y_BASE } from '../../Recepcion/reportes/chartBase';
import { COLOR, FUENTE } from '../../Recepcion/reportes/tokens';

const fechaCorta = (iso) => {
    if (!iso) return '';
    const [a, m, d] = String(iso).slice(0, 10).split('-');
    return `${d}/${m}/${a}`;
};

/**
 * Todos los turnos agendados por tipo de trámite (pedido del estudio, 15-oct-2026). El gráfico
 * de "Qué se presenta y cómo sale" solo ve lo que ya pasó (el período se corta en hoy); éste
 * muestra la agenda completa hasta fin de mes, separando lo que ya pasó de lo que está por delante.
 * Sin las idas canceladas. Una ida con dos trámites cuenta en los dos tipos.
 *
 * @param {{data: Array<{tipoTramite, cantidad, yaPasaron, porDelante}>, desde: string, hasta: string}} props
 */
const AgendadosPorTipoChart = ({ data, desde, hasta }) => {
    const filas = useMemo(() => data || [], [data]);
    const total = filas.reduce((acc, f) => acc + (f.cantidad || 0), 0);

    const chartData = {
        labels: filas.map((f) => (f.tipoTramite || 'Sin tipo').replaceAll('_', ' ')),
        datasets: [
            { label: 'Ya pasaron', data: filas.map((f) => f.yaPasaron || 0), backgroundColor: COLOR.azul, borderRadius: 4, barThickness: 20 },
            { label: 'Por delante', data: filas.map((f) => f.porDelante || 0), backgroundColor: COLOR.gris, borderRadius: 4, barThickness: 20 },
        ],
    };

    const options = {
        ...BASE_OPTIONS,
        indexAxis: 'y',
        scales: {
            x: { ...EJE_Y_BASE, stacked: true, beginAtZero: true, ticks: { ...EJE_Y_BASE.ticks, precision: 0 } },
            y: { ...EJE_X_BASE, stacked: true },
        },
        plugins: {
            ...BASE_OPTIONS.plugins,
            tooltip: {
                ...BASE_OPTIONS.plugins.tooltip,
                callbacks: {
                    afterBody: (items) => {
                        const fila = filas[items[0].dataIndex];
                        return fila ? `Total agendados: ${fila.cantidad}` : '';
                    },
                },
            },
        },
    };

    return (
        <Card variant="outlined" sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: 'none', padding: '18px 20px' }}>
            <Typography sx={{ fontFamily: FUENTE, fontSize: 15, fontWeight: 700, color: COLOR.negro, marginBottom: '2px' }}>
                Agendados por tipo de trámite{total > 0 ? ` · ${total}` : ''}
            </Typography>
            <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                Todos los turnos agendados {desde && hasta ? `del ${fechaCorta(desde)} al ${fechaCorta(hasta)}` : 'del período'}
                {' '}(hasta fin de mes), presentados o no. Sin los cancelados.
            </Typography>
            {filas.length === 0 ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisFrio, padding: '32px 0', textAlign: 'center' }}>
                    No hay turnos agendados en el período.
                </Typography>
            ) : (
                <Box sx={{ height: Math.max(180, filas.length * 46) }}>
                    <Bar data={chartData} options={options} />
                </Box>
            )}
        </Card>
    );
};

export default AgendadosPorTipoChart;
